<template>
  <v-container class="fill-height" style="opacity: 0.94" fluid>
    <v-sheet elevation="1" rounded width="100%" class="mt-16 mb-8">
      <v-row justify="space-between" class="pa-2">
        <v-col cols="12" md="6">
          <v-text-field
            :label="$t('Search')"
            prepend-icon="mdi-magnify"
            v-model="search"
          ></v-text-field>
        </v-col>
      </v-row>
      <v-row justify="space-around" class="pa-2">
        <v-col cols="6" class="text-end">
          <v-form ref="certForm" :disabled="!selectedColor">
            <section v-if="data.variant === 'binary'">
              <text-field
                v-model="data.registerName1"
                :required="data.variant === 'binary'"
                clearable
                :label="$t('Name of the first Star')"
              />
              <text-field
                v-model="data.registerName2"
                :required="data.variant === 'binary'"
                clearable
                :label="$t('Name of the second Star')"
              />
            </section>
            <text-field
              v-else
              v-model="data.registerName"
              :required="data.variant !== 'binary'"
              clearable
              :label="$t('Name of the Star')"
            />
            <v-row justify="space-between">
              <v-col cols="8">
                <text-field
                  :label="$t('Registry No.')"
                  v-model="data.registryKey"
                  required
                  clearable
                  :hint="$t('Registry Overwrite Warning')"
                />
              </v-col>
              <v-col cols="4" class="end">
                <v-checkbox v-model="data.register" :label="$t('Register in Software')" />
              </v-col>
            </v-row>
            <date-picker
              v-model="data.registerDate"
              :label="$t('Date of the Star Naming')"
              format="DD.MM.YYYY"
              required
            />
            <v-select
              :items="[
                $t('Standard'),
                'DD-MM-YYYY',
                'MM-DD-YYYY',
                'DD.MM.YYYY',
                'YYYY-MM-DD',
                'YYYY/MM/DD',
                'DD/MM/YYYY',
                'DD-MMM-YYYY',
                'YYYY年MM月DD日',
                'DD/MMM/YYYY',
                'D MMMM YYYY',
              ]"
              v-model="data.format"
              outlined
              clearable
            >
              <template #label>
                {{ $t('Format of the Date') }}
                <span class="red--text"><strong>*</strong></span>
              </template>
            </v-select>

            <v-select
              v-model="data.registeredBy"
              :items="regBys"
              :label="$t('Registered By')"
              outlined
              clearable
            >
              <template #label>
                {{ $t('Registered By') }}
                <span class="red--text"><strong>*</strong></span>
              </template>
            </v-select>
            <v-radio-group v-model="data.variant">
              <v-row no-gutters dense align="center" justify="start">
                <v-col> <v-radio :label="$t('Single Star')" value="single" checked /> </v-col>
                <v-col> <v-radio :label="$t('Constellation Star')" value="special" /> </v-col>
                <v-col> <v-radio :label="$t('Binary Star')" value="binary" /> </v-col>
              </v-row>
            </v-radio-group>

            <v-radio-group v-model="data.brightness">
              <v-row no-gutters dense align="center" justify="start">
                <v-col>
                  <v-radio :label="$t('Visible Star')" value="low" checked />
                </v-col>
                <v-col> <v-radio :label="$t('Bright Star')" value="medium" /> </v-col>
                <v-col>
                  <v-radio :label="$t('Very Bright Star')" value="high" />
                </v-col>
              </v-row>
            </v-radio-group>

            <v-checkbox
              v-model="customCoordinates"
              :label="$t('Custom Coordinates')"
              :hint="$t('\'Register in Software\' not supported for custom coordinates')"
              persistent-hint
              class="mb-2"
            ></v-checkbox>

            <section v-if="customCoordinates">
              <v-row dense>
                <v-col>
                  <text-field
                    v-model="data.coords.lat"
                    clearable
                    :label="`Lattitude${data.variant === 'binary' ? ' 1' : ''}`"
                  />
                </v-col>
                <v-col>
                  <text-field
                    v-model="data.coords.long"
                    clearable
                    :label="`Longitude${data.variant === 'binary' ? ' 1' : ''}`"
                  />
                </v-col>
              </v-row>
              <v-row class="my-0 py-0" dense v-if="data.variant === 'binary'">
                <v-col>
                  <text-field v-model="data.coords_2.lat" clearable label="Lattitude 2" />
                </v-col>
                <v-col>
                  <text-field v-model="data.coords_2.long" clearable label="Longitude 2" />
                </v-col>
              </v-row>
            </section>

            <section v-else>
              <v-combobox
                v-if="data.variant === 'special'"
                :items="filteredConstellations"
                v-model="selectedConstellation"
                item-text="label"
                item-value="value"
                outlined
                clearable
              >
                <template #label>
                  {{ $t('Constellation') }}
                  <span class="red--text"><strong>*</strong></span>
                </template>
                <template #item="{ item }">
                  <v-row class="text-start">
                    <v-col cols="2" v-if="item.icon">
                      <v-icon>{{ item.icon }}</v-icon>
                    </v-col>
                    <v-col>{{ $t(item.label) }}</v-col>
                  </v-row>
                </template>
              </v-combobox>

              <v-row no-gutters>
                <v-col>
                  <v-combobox
                    :items="filteredCatalogue"
                    v-model="selectedStar"
                    item-text="name"
                    item-value="name"
                    no-data-text="No star found for this configuration"
                    outlined
                    clearable
                  >
                    <template #label>
                      {{ $t('Select Star') }}
                      <span class="red--text"><strong>*</strong></span>
                    </template>
                    <template #item="{ item, on, attrs }">
                      <v-list-item class="text-start" v-on="on" v-bind="attrs">
                        <v-list-item-icon v-if="item.variant === 'SUPERNOVA'">
                          <v-icon>
                            {{ `mdi-zodiac-${item.constellation.toLowerCase()}` }}
                          </v-icon>
                        </v-list-item-icon>

                        <v-list-item-content>
                          <v-list-item-title>{{ item.name }}</v-list-item-title>
                          <v-list-item-subtitle class="overline">
                            {{ $t(item.brightness) }}
                          </v-list-item-subtitle>
                        </v-list-item-content>

                        <v-list-item-icon>
                          <v-btn color="warning" class="black--text overline" elevation="2" x-small>
                            {{ $t(item.variant) }}
                          </v-btn>
                        </v-list-item-icon>
                      </v-list-item>
                    </template>
                  </v-combobox>
                </v-col>
                <v-col cols="1">
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn
                        v-bind="attrs"
                        v-on="on"
                        class="mx-2"
                        fab
                        :disabled="validateAutoStarInput()"
                        @click="assignRandomStar()"
                      >
                        <v-icon dark>mdi-refresh-auto</v-icon>
                      </v-btn>
                    </template>
                    <span>{{ $t('Assign a random star') }}</span>
                  </v-tooltip>
                </v-col>
              </v-row>
            </section>

            <v-select
              :items="filteredLangs"
              v-model="data.lang"
              item-text="label"
              item-value="value"
              outlined
              clearable
            >
              <template #label>
                {{ $t('Language') }}
                <span class="red--text"><strong>*</strong></span>
              </template>
            </v-select>
            <v-list class="mb-5" outlined nav v-if="selectedColor">
              <v-subheader>{{ $t('Type of Certificate') }}</v-subheader>
              <v-list-item-group v-model="data.type" color="primary">
                <v-row justify="space-between">
                  <v-col cols="6" v-for="t in types" :key="t.value">
                    <v-list-item :value="t.value">
                      <v-img
                        :src="getImageSrc(selectedColor, t.value === 'print')"
                        class="white--text align-end"
                        gradient="to bottom, rgba(0,0,0,.1), rgba(0,0,0,.5)"
                        height="255px"
                        contain
                      >
                        <v-card-title>{{ $t(t.label) }}</v-card-title>
                      </v-img>
                    </v-list-item>
                  </v-col>
                </v-row>
              </v-list-item-group>
            </v-list>
            <v-btn
              :loading="loading"
              :disabled="!isValidCert"
              @click="generateCert"
              color="primary"
            >
              <template #loader>
                <span class="custom-loader">
                  <v-icon light>mdi-loading</v-icon>
                </span>
              </template>
              {{ $t('Save & Preview') }}
            </v-btn>
          </v-form>
        </v-col>
        <v-col cols="5">
          <v-list outlined nav>
            <v-subheader>{{ $t('Color of the Certificate') }}</v-subheader>
            <v-list-item-group v-model="selectedColor" color="primary">
              <v-list-item v-for="(color, i) in colors" :key="i" :value="color">
                <certificate-card
                  :subtitle="getLangs(color)"
                  :title="$t(color.title)"
                  :img="getImageSrc(color)"
                  :third="`${(color.metadata && color.metadata.orientation) || ''}, ${
                    (color.metadata && color.metadata.size) || ''
                  }`"
                />
              </v-list-item>
            </v-list-item-group>
          </v-list>
        </v-col>
      </v-row>
    </v-sheet>
  </v-container>
</template>

<script>
  import api from '@/assets/js/api_helper'
  import swh from '@/assets/js/sw_helper'
  import utils from '@/assets/js/utils'
  import certificateCard from '../components/CertificateCard'
  import datePicker from '../components/DatePicker'
  import TextField from '../components/TextField'
  import { createCustomCert } from '../services/pdfGenerationService'
  import { getStoresProtected } from '../services/storeService'
  import { getUserRole } from '../services/authService'

  export default {
    props: {
      source: String,
    },
    data() {
      return {
        search: '',
        catalogue: [],
        constellations: [
          {
            label: 'ARIES (Mar 21 - Apr 19)',
            icon: 'mdi-zodiac-aries',
            value: 'aries',
          },
          {
            label: 'TAURUS (Apr 20 - May 20)',
            icon: 'mdi-zodiac-taurus',
            value: 'taurus',
          },
          {
            label: 'GEMINI (May 21 - Jun 20)',
            icon: 'mdi-zodiac-gemini',
            value: 'gemini',
          },
          {
            label: 'CANCER (Jun 21 - Jul 22)',
            icon: 'mdi-zodiac-cancer',
            value: 'cancer',
          },
          {
            label: 'LEO (Jul 23 - Aug 22)',
            icon: 'mdi-zodiac-leo',
            value: 'leo',
          },
          {
            label: 'VIRGO (Aug 23 - Sep 22)',
            icon: 'mdi-zodiac-virgo',
            value: 'virgo',
          },
          {
            label: 'LIBRA (Sep 23 - Oct 22)',
            icon: 'mdi-zodiac-libra',
            value: 'libra',
          },
          {
            label: 'SCORPIO (Oct 23 - Nov 21)',
            icon: 'mdi-zodiac-scorpio',
            value: 'scorpio',
          },
          {
            label: 'SAGITTARIUS (Nov 22 - Dec 21)',
            icon: 'mdi-zodiac-sagittarius',
            value: 'sagittarius',
          },
          {
            label: 'CAPRICORN (Dec 22 - Jan 19)',
            icon: 'mdi-zodiac-capricorn',
            value: 'capricorn',
          },
          {
            label: 'AQUARIUS (Jan 20 - Feb 18)',
            icon: 'mdi-zodiac-aquarius',
            value: 'aquarius',
          },
          {
            label: 'PISCES (Feb 19 - Mar 20)',
            icon: 'mdi-zodiac-pisces',
            value: 'pisces',
          },
          { label: 'ORION', value: 'orion' },
          { label: 'THE BIG DIPPER / URSA MAJOR', value: 'ursa major' },
          {
            label: 'THE LITTLE DIPPER / URSA MINOR',
            value: 'ursa minor',
          },
        ],
        constellationsCN: [
          { label: '仙女座 | ANDROMEDA', value: 'andromeda' },
          { label: '唧筒座 | ANTLIA', value: 'antlia' },
          { label: '天燕座 | APUS', value: 'apus' },
          { label: '水瓶座 | AQUARIU', value: 'aquarius' },
          { label: '天鹰座 | AQUILA', value: 'aquila' },
          { label: '天坛座 | ARA', value: 'ara' },
          { label: '白羊座 | ARIES', value: 'aries' },
          { label: '御夫座 | AURIGA', value: 'auriga' },
          { label: '牧夫座 | BOOTES', value: 'bootes' },
          { label: '雕具座 | CAELUM', value: 'caelum' },
          { label: '鹿豹座 | CAMELOPARDALIS', value: 'camelopardalis' },
          { label: '巨蟹座 | CANCER', value: 'cancer' },
          { label: '猎犬座 | CANES VENATICI', value: 'canes venatici' },
          { label: '大犬座 | CANIS MAJOR', value: 'canis major' },
          { label: '小犬座 | CANIS MINOR', value: 'canis minor' },
          { label: '摩羯座 | CAPRICORNUS', value: 'capricorn' },
          { label: '船底座 | CARINA', value: 'carina' },
          { label: '仙后座 | CASSIOPEIA', value: 'cassiopeia' },
          { label: '半人马座 | CENTAURUS', value: 'centaurus' },
          { label: '仙王座 | CEPHEUS', value: 'cepheus' },
          { label: '鲸鱼座 | CETUS', value: 'cetus' },
          { label: '堰蜓座 | CHAMAELEON', value: 'chamaeleon' },
          { label: '圆规座 | CIRCINUS', value: 'circinus' },
          { label: '天鸽座 | COLUMBA', value: 'columba' },
          { label: '后发座 | COMA BERENICES', value: 'coma berenices' },
          { label: '南冕座 | CORONA AUSTRALIS', value: 'corona australis' },
          { label: '北冕座 | CORONA BOREALIS', value: 'corona borealis' },
          { label: '乌鸦座 | CORVUS', value: 'corvus' },
          { label: '巨爵座 | CRATER', value: 'crater' },
          { label: '南十字座 | CRUX', value: 'crux' },
          { label: '天鹅座 | CYGNUS', value: 'cygnus' },
          { label: '海豚座 | DELPHINUS', value: 'delphinus' },
          { label: '剑鱼座 | DORADO', value: 'dorado' },
          { label: '天龙座 | DRACO', value: 'draco' },
          { label: '小马座 | EQUULEUS', value: 'equuleus' },
          { label: '波江座 | ERIDANUS', value: 'eridanus' },
          { label: '天炉座 | FORNAX', value: 'fornax' },
          { label: '双子座 | GEMINI', value: 'gemini' },
          { label: '天鹤座 | GRUS', value: 'grus' },
          { label: '武仙座 | HERCULES', value: 'hercules' },
          { label: '时钟座 | HOROLOGIUM', value: 'horologium' },
          { label: '长蛇座 | HYDRA', value: 'hydra' },
          { label: '水蛇座 | HYDRUS', value: 'hydrus' },
          { label: '印第安座 | INDUS', value: 'indus' },
          { label: '蝎虎座 | LACERTA', value: 'lacerta' },
          { label: '狮子座 | LEO', value: 'leo' },
          { label: '小狮座 | LEO MINOR', value: 'leo minor' },
          { label: '天兔座 | LEPUS', value: 'lepus' },
          { label: '天秤座 | LIBRA', value: 'libra' },
          { label: '豺狼座 | LUPUS', value: 'lupus' },
          { label: '天猫座 | LYNX', value: 'lynx' },
          { label: '天琴座 | LYRA', value: 'lyra' },
          { label: '山案座 | MENSA', value: 'mensa' },
          { label: '显微镜座 | MICROSCOPIUM', value: 'microscopium' },
          { label: '麒麟座 | MONOCEROS', value: 'monoceros' },
          { label: '苍蝇座 | MUSCA', value: 'musca' },
          { label: '矩尺座 | NORMA', value: 'norma' },
          { label: '南极座 | OCTANS', value: 'octans' },
          { label: '蛇夫座 | OPHIUCHUS', value: 'ophiuchus' },
          { label: '猎户座 | ORION', value: 'orion' },
          { label: '孔雀座 | PAVO', value: 'pavo' },
          { label: '飞马座 | PEGASUS', value: 'pegasus' },
          { label: '英仙座 | PERSEUS', value: 'perseus' },
          { label: '凤凰座 | PHOENIX', value: 'phoenix' },
          { label: '绘架座 | PICTOR', value: 'pictor' },
          { label: '双鱼座 | PISCES', value: 'pisces' },
          { label: '南鱼座 | PISCIS AUSTRALIS', value: 'piscis australis' },
          { label: '船尾座 | PUPPIS', value: 'puppis' },
          { label: '罗盘座 | PYX', value: 'pyx' },
          { label: '网罟座 | RETICULUM', value: 'reticulum' },
          { label: '天箭座 | SAGITTA', value: 'sagitta' },
          { label: '射手座 | SAGITTARIUS', value: 'sagittarius' },
          { label: '天蝎座 | SCORPIUS', value: 'scorpius' },
          { label: '玉夫座 | SCULPTOR', value: 'sculptor' },
          { label: '盾牌座 | SCUTUM', value: 'scutum' },
          { label: '巨蛇座头 | SERPENS CAPUT', value: 'serpens caput' },
          { label: '巨蛇座尾 | SERPENS CAUDA', value: 'serpens cauda' },
          { label: '六分仪座 | SEXTANS', value: 'sextans' },
          { label: '金牛座 | TAURUS', value: 'taurus' },
          { label: '望远镜座 | TELESCOPIUM', value: 'telescopium' },
          { label: '三角座 | TRIANGULUM', value: 'triangulum' },
          { label: '南三角座 | TRIANGULUM AUSTRALE', value: 'triangulum australe' },
          { label: '杜鹃座 | TUCANA', value: 'tucana' },
          { label: '大熊座 | URSA MAJOR', value: 'the big dipper' },
          { label: '小熊座 | URSA MINOR', value: 'the little dipper' },
          { label: '船帆座 | VELA', value: 'vela' },
          { label: '室女座 | VIRGO', value: 'virgo' },
          { label: '飞鱼座 | VOLANS', value: 'volans' },
          { label: '狐狸座 | VULPECULA', value: 'vulpecula' },
        ],
        dateMenu: false,
        selectedColor: null,
        selectedStar: null,
        selectedConstellation: null,
        customCoordinates: false,
        loading: false,
        regBys: [],
        data: {
          registerName: '',
          registerDate: new Date().toISOString().substring(0, 10),
          registryKey: '',
          registeredBy: '',
          register: true,
          coords: { lat: '', long: '' },
          coords_2: { lat: '', long: '' },
          variant: 'single',
          brightness: 'low',
          format: this.$i18n.t('Standard'),
          lang: null,
          type: null,
        },
        colors: [],
        langs: [
          { label: this.$t('English / EN'), value: 'EN' },
          { label: this.$t('Deutsch / DE'), value: 'DE' },
          { label: this.$t('Italian / IT'), value: 'IT' },
          { label: this.$t('Spanish / ES'), value: 'ES' },
          { label: this.$t('French / FR'), value: 'FR' },
          { label: this.$t('Deutsch / NL'), value: 'NL' },
          { label: this.$t('Chinese / CN'), value: 'CN' },
          { label: this.$t('English / EN'), value: 'EN' },
          { label: this.$t('Deutsch / DE'), value: 'DE' },
          { label: this.$t('Italian / IT'), value: 'IT' },
          { label: this.$t('Spanish / ES'), value: 'ES' },
          { label: this.$t('French / FR'), value: 'FR' },
          { label: this.$t('Deutsch / NL'), value: 'NL' },
          { label: this.$t('Chinese / CN'), value: 'CN' },
        ],
        types: [
          { label: 'Print Ready', value: 'print' },
          { label: 'Digital Version', value: 'digital' },
        ],
        apiUrl: `${process.env.VUE_APP_YOUVENDO_API_SERVER}/`,
      }
    },
    methods: {
      parseDate(dateString) {
        return swh.parseDate(dateString)
      },
      getImageSrc(color, print = false) {
        return utils.getImageSrc(color, null, null, print)
      },
      getLangs(color) {
        return Object.entries(color.value)
          .reduce((langs, [lang, value]) => {
            if (value !== '') {
              langs.push(lang.toUpperCase())
            }
            return langs
          }, [])
          .join(' / ')
      },
      async generateCert() {
        this.loading = true

        console.log(this.selectedColor)
        console.log(this.data)
        console.log(this.data.type)
        console.log(this.data.lang)

        try {
          const { data } = await createCustomCert(
            this.data,
            this.selectedColor.id,
            this.data.type,
            this.data.lang
          )

          // { files, registered }: registered is null when not requested,
          // false when requested and it failed — the certificate still came back.
          data.files.forEach((item) => {
            if (typeof item === 'string') {
              window.open(this.apiUrl + item, '_blank')
            } else {
              window.open(item.url, '_blank')
            }
          })
          if (data.registered === false) {
            this.$store.commit('showSnackbar', {
              message: 'Certificate created, but registering it failed — please register the star manually.',
              color: 'warning',
            })
          }
        } catch (err) {
          console.log(err)
        } finally {
          this.loading = false
        }
      },

      validateAutoStarInput() {
        if (this.data.variant === 'special') {
          return this.selectedConstellation === null
        }
        return !this.selectedColor
      },

      assignRandomStar() {
        console.log('assignRandomStar called')
        console.log('filteredCatalogue: ', this.filteredCatalogue)
        this.selectedStar =
          this.filteredCatalogue[Math.floor(Math.random() * this.filteredCatalogue.length)]
      },
    },
    watch: {
      selectedStar(val) {
        console.log('selectedStar: ', val)
        const coords = val.coords.split('  ')
        const coords_2 = val.coords2?.split('  ')

        this.data.coords = {
          lat: coords[0].trim(),
          long: coords[1].trim(),
        }

        if (coords_2) {
          this.data.coords_2 = {
            lat: coords_2[0].trim(),
            long: coords_2[1].trim(),
          }
        }

        this.data.starName = val.name
      },
      customCoordinates(val) {
        if (!val && this.selectedStar !== null) this.data.starName = this.selectedStar.name
        else delete this.data.starName
      },
      selectedColor(val) {
        console.log('selectedColor: ', val)
      },
      filteredLangs(val) {
        console.log('filtered langs changed')
        if (val?.length !== 0) this.data.lang = val[0]?.value
      },
    },
    mounted: async function () {
      // The catalogue endpoint is paginated ({ items, total }, max 500 per
      // request) — page through it to get the full assignable pool.
      const catalogue = []
      let total = Infinity
      while (catalogue.length < total) {
        const { data } = await api.getStarCatalogue({ limit: 500, offset: catalogue.length })
        catalogue.push(...data.items)
        total = data.total
        if (!data.items.length) break
      }
      this.catalogue = catalogue

      console.log('Catalogue: ', this.catalogue)

      const stores = await getStoresProtected()
      this.regBys = stores.map((s) => s.domain)
      if (this.regBys.length > 0) this.data.registeredBy = this.regBys[0]

      const colors = stores.reduce((acc, store) => {
        store.colors.forEach((color) => {
          if (color.id) {
            acc[color.id] = acc[color.id] || { ...color, value: {} }
            acc[color.id].value[store.lang] = color.title
          }
        })
        return acc
      }, {})

      const userRole = await getUserRole()
      if (userRole === 'moderator') {
        Object.values(colors).forEach((color) => {
          if (color.title === '经典午夜蓝色') color.value['EN'] = 'modern nightblue'
          else if (color.title === '星辰璀璨色') color.value['EN'] = 'galaxy purple dream'
        })
      }

      this.colors = Object.values(colors)
    },
    computed: {
      isValidCert: function () {
        let valid = true
        if (!this.data.registryKey) valid = false
        if (!this.data.registerDate) valid = false
        if (!this.selectedColor) valid = false
        if (!this.data.lang) valid = false
        if (!this.data.type) valid = false
        return valid
      },
      filteredCatalogue: function () {
        return this.catalogue.filter((sso) => {
          // Check brightness matches
          if (sso?.brightness?.toLowerCase() !== this.data.brightness) return false

          const variant = sso.variant?.toLowerCase()
          const currentVariant = this.data.variant

          switch (currentVariant) {
            case 'special':
              return (
                sso.constellation?.toLowerCase() ===
                this.selectedConstellation?.value?.toLowerCase()
              )
            case 'binary':
              return variant === 'binary'
            case 'single':
              return variant === 'single'
            default:
              return false
          }
        })
      },
      filteredConstellations: function () {
        return this.data.registeredBy === 'www.cn.star-naming.com'
          ? this.constellationsCN
          : this.constellations
      },
      filteredLangs() {
        console.log('get filtered langs called')
        const color = this.selectedColor
        if (!color || !color.value) return this.langs
        const langs = this.langs.filter((l) => Object.keys(color.value).includes(l.value))
        return langs
      },
    },
    components: { certificateCard, datePicker, TextField },
  }
</script>

<style scoped>
  .centered-input > input {
    text-align: center;
  }

  .custom-loader {
    transform: scaleX(-1);
    -moz-transform: scaleX(-1);
    -webkit-transform: scaleX(-1);
    -ms-transform: scaleX(-1);
    animation: loader 1s infinite;
    display: flex;
  }
  @-moz-keyframes loader {
    from {
      transform: rotate(0);
    }
    to {
      transform: rotate(360deg);
    }
  }
  @-webkit-keyframes loader {
    from {
      transform: rotate(0);
    }
    to {
      transform: rotate(360deg);
    }
  }
  @-o-keyframes loader {
    from {
      transform: rotate(0);
    }
    to {
      transform: rotate(360deg);
    }
  }
  @keyframes loader {
    from {
      transform: rotate(0);
    }
    to {
      transform: rotate(360deg);
    }
  }
</style>
